/**
 * Server-Sent Events (SSE) Service
 * Manages EventSource connections for real-time updates
 */

class SSEService {
  constructor() {
    this.eventSource = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.maxReconnectDelay = 30000; // Max 30 seconds
    this.listeners = new Map();
    this.connectionState = 'disconnected'; // disconnected, connecting, connected, error
    this.stateListeners = new Set();
  }

  /**
   * Connect to SSE endpoint
   * @param {string} endpoint - The SSE endpoint URL
   * @param {Object} options - Connection options
   */
  connect(endpoint, options = {}) {
    if (this.eventSource && this.eventSource.readyState !== EventSource.CLOSED) {
      console.log('SSE: Already connected or connecting');
      return;
    }

    this.setConnectionState('connecting');

    // Add authentication token as query parameter
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    const separator = endpoint.includes('?') ? '&' : '?';
    const authenticatedEndpoint = token ? `${endpoint}${separator}token=${encodeURIComponent(token)}` : endpoint;

    console.log(`SSE: Connecting to ${authenticatedEndpoint}`);

    try {
      this.eventSource = new EventSource(authenticatedEndpoint, {
        ...options
      });

      this.eventSource.onopen = (event) => {
        console.log('SSE: Connection opened');
        this.setConnectionState('connected');
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000; // Reset delay
      };

      this.eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error('SSE: Error parsing message:', error);
        }
      };

      this.eventSource.onerror = (event) => {
        console.error('SSE: Connection error:', event);
        this.setConnectionState('error');

        if (this.eventSource.readyState === EventSource.CLOSED) {
          this.handleReconnect(endpoint, options);
        }
      };

      // Handle custom event types
      this.setupCustomEventListeners();

    } catch (error) {
      console.error('SSE: Failed to create EventSource:', error);
      this.setConnectionState('error');
      this.handleReconnect(endpoint, options);
    }
  }

  /**
   * Setup listeners for custom event types
   */
  setupCustomEventListeners() {
    if (!this.eventSource) return;

    // Security registry events
    this.eventSource.addEventListener('registryChanged', (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleMessage(data, 'registryChanged');
      } catch (error) {
        console.error('SSE: Error parsing registryChanged event:', error);
      }
    });

    // Heartbeat events
    this.eventSource.addEventListener('heartbeat', (event) => {
      console.log('SSE: Heartbeat received');
    });
  }

  /**
   * Handle incoming messages
   * @param {Object} data - Message data
   * @param {string} eventType - Event type
   */
  handleMessage(data, eventType = 'message') {
    console.log(`SSE: Received ${eventType}:`, data);

    // Notify all listeners for this event type
    const eventListeners = this.listeners.get(eventType) || new Set();
    eventListeners.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`SSE: Error in ${eventType} listener:`, error);
      }
    });
  }

  /**
   * Add event listener
   * @param {string} eventType - Event type to listen for
   * @param {Function} callback - Callback function
   */
  addEventListener(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType).add(callback);
  }

  /**
   * Remove event listener
   * @param {string} eventType - Event type
   * @param {Function} callback - Callback function to remove
   */
  removeEventListener(eventType, callback) {
    const eventListeners = this.listeners.get(eventType);
    if (eventListeners) {
      eventListeners.delete(callback);
      if (eventListeners.size === 0) {
        this.listeners.delete(eventType);
      }
    }
  }

  /**
   * Add connection state listener
   * @param {Function} callback - Callback function
   */
  addStateListener(callback) {
    this.stateListeners.add(callback);
  }

  /**
   * Remove connection state listener
   * @param {Function} callback - Callback function
   */
  removeStateListener(callback) {
    this.stateListeners.delete(callback);
  }

  /**
   * Set connection state and notify listeners
   * @param {string} state - New connection state
   */
  setConnectionState(state) {
    if (this.connectionState !== state) {
      this.connectionState = state;
      console.log(`SSE: Connection state changed to ${state}`);

      this.stateListeners.forEach(callback => {
        try {
          callback(state);
        } catch (error) {
          console.error('SSE: Error in state listener:', error);
        }
      });
    }
  }

  /**
   * Handle reconnection logic
   * @param {string} endpoint - The SSE endpoint URL (original endpoint without token)
   * @param {Object} options - Connection options
   */
  handleReconnect(endpoint, options) {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('SSE: Max reconnection attempts reached');
      this.setConnectionState('error');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), this.maxReconnectDelay);

    console.log(`SSE: Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    setTimeout(() => {
      // Use the original endpoint for reconnection (token will be re-added in connect method)
      this.connect(endpoint, options);
    }, delay);
  }

  /**
   * Disconnect from SSE
   */
  disconnect() {
    if (this.eventSource) {
      console.log('SSE: Disconnecting');
      this.eventSource.close();
      this.eventSource = null;
      this.setConnectionState('disconnected');
      this.reconnectAttempts = 0;
    }
  }

  /**
   * Get current connection state
   * @returns {string} Connection state
   */
  getConnectionState() {
    return this.connectionState;
  }

  /**
   * Check if connected
   * @returns {boolean} True if connected
   */
  isConnected() {
    return this.connectionState === 'connected';
  }

  /**
   * Get EventSource ready state
   * @returns {number|null} Ready state or null if no connection
   */
  getReadyState() {
    return this.eventSource ? this.eventSource.readyState : null;
  }
}

// Create singleton instance
const sseService = new SSEService();

export default sseService;
